import React from 'react';
import { Flex, Box } from '../Rebass';
import styled from '@emotion/styled';

import theme from '../../theme';
import Heading from '../Heading';
import Text from '../Text';
import Button from '../Button';
import { AnimatePresence, motion } from '../Motion';
import { SxStyleProp } from '../Rebass/types';
interface ModalProps {
  size: 'small' | 'medium' | 'large';
  isOpen: boolean;
  title?: string;
  subtitle?: string;
  handleClose: () => void;
  modalFooter?: () => React.ReactNode;
  bg?: string;
  children?: React.ReactNode;
  sx?: SxStyleProp;
  containerSx?: SxStyleProp;
  contentProps?: any;
  isPageModal?: boolean;
  closeText?: string;
  maxWidth?: string;
  hasCloseButton?: boolean;
}

export const Overlay = styled(Box)`
  width: 100%;
  height: 100%;
  background: rgba(31, 31, 31, 0.8);
  position: fixed;
  top: 0;
  left: 0;
`;

interface ModalContainerProps {
  width?: string;
  maxHeight?: string;
  minHeight?: string;
  borderRadius?: string;
}

export const ModalContainer = styled(Box)(
  ({ width, maxHeight, minHeight, borderRadius }: ModalContainerProps) => `
  ${!!width ? `width: ${width};` : ''}
  ${!!maxHeight ? `max-height: ${maxHeight};` : ''}
  ${!!minHeight ? `min-height: ${minHeight};` : ''}
  ${!!borderRadius ? `border-radius: ${borderRadius};` : ''}
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);

  @media(max-width: ${theme.breakpoints[2]}) {
    width: 100%;
    min-height: 100%;
  }
`,
);
const AnimatedModal = motion.custom(ModalContainer);

const configMap = {
  small: {
    width: '40%',
    borderRadius: '4px',
    height: '80%',
  },
  medium: {
    width: '50%',
    borderRadius: '4px',
    height: '80%',
  },
  large: {
    width: '100%',
    borderRadius: '0px',
    height: '100%',
  },
};

const StyledHeader = styled(Flex)<{ bg: string }>`
  position: sticky;
  top: 0;
  left: 0;
  border-bottom: 1px solid ${theme.colors.baseGrayVeryLight};
  z-index: 1000;
  align-items: center;
  background-color: ${(props) => props.bg};
`;

const StyledFooter = styled(Box)`
  position: sticky;
  bottom: 0;
  left: 0;
  border-top: 1px solid ${theme.colors.baseGrayVeryLight};
`;

const animationVariants = {
  enter: (isLarge) => ({
    y: isLarge ? '-50%' : '-50%',
    opacity: 1,
    transition: {
      type: 'spring',
      damping: 15,
      stiffness: 50,
    },
  }),
  initial: (isLarge) => ({
    y: isLarge ? '-50%' : '-40%',
    opacity: 0,
  }),
  exit: {
    opacity: 0,
    transition: {
      duration: 0.3,
    },
  },
};

export const LargeHeader = ({
  title,
  subtitle,
  handleClose,
  bg,
  closeText = '',
  hasCloseButton = true,
}) => (
  <StyledHeader id="large-modal-header" py={[2]} px={[4, 4, 10]} bg={bg}>
    <Flex
      maxWidth={'1280px'}
      justifyContent="space-between"
      width="100%"
      mx="auto"
      alignItems="baseline"
    >
      <Box>
        <Heading size="h6">{title}</Heading>
        {subtitle ? (
          <Text color="brandBrown" size="tiny">
            {subtitle}
          </Text>
        ) : null}
      </Box>
      {hasCloseButton && (
        <Button
          data-testid="modal-close"
          variant="text"
          icon="close"
          iconPosition="right"
          onClick={handleClose}
        >
          {closeText ? closeText : 'Close'}
        </Button>
      )}
    </Flex>
  </StyledHeader>
);

const SmallHeader = ({
  title,
  subtitle,
  handleClose,
  bg,
  hasCloseButton = true,
}) => (
  <StyledHeader py={[2]} pl={[5]} pr={[2]} bg={bg}>
    <Flex width="100%" justifyContent="space-between" alignItems="center">
      <Box>
        <Heading size="h6">{title}</Heading>
        {subtitle ? (
          <Text color="brandBrown" size="tiny">
            {subtitle}
          </Text>
        ) : null}
      </Box>
      {hasCloseButton && (
        <Button
          data-testid="modal-close"
          variant="text"
          icon="close"
          iconPosition="right"
          onClick={handleClose}
        />
      )}
    </Flex>
  </StyledHeader>
);

const ConditionalAnimation = ({ isPageModal, children, isLarge }) => {
  return isPageModal ? (
    <>{children}</>
  ) : (
    // @ts-ignore
    <AnimatePresence initial={false} custom={isLarge}>
      {children}
    </AnimatePresence>
  );
};

const Modal = ({
  size = 'medium',
  modalFooter,
  children,
  isOpen,
  handleClose,
  title,
  subtitle,
  bg = 'baseWhite',
  sx = {},
  containerSx = {},
  contentProps = {},
  isPageModal = false,
  maxWidth = '1280px',
  closeText = '',
  hasCloseButton = true,
}: ModalProps) => {
  const { width, height, borderRadius } = configMap[size];
  const isLarge = size === 'large';

  const style = { zIndex: 'modal', ...sx };

  return (
    <ConditionalAnimation isPageModal={isPageModal} isLarge={isLarge}>
      {isOpen ? (
        <Overlay
          data-testid="modal-overlay"
          onClick={() => (!isLarge ? handleClose() : null)}
          sx={style}
        >
          <AnimatedModal
            id="modal-container"
            data-testid="modal-container"
            key="modal-container"
            width={width}
            height={height}
            borderRadius={borderRadius}
            bg={bg}
            onClick={(e) => e.stopPropagation()}
            variants={animationVariants}
            style={{ x: '-50%' }}
            animate="enter"
            initial="initial"
            exit="exit"
            custom={isLarge}
          >
            <Flex
              id="modal-scrollable-content"
              data-testid="modal-scrollable-content"
              flexDirection="column"
              height="100%"
              sx={{ overflowY: 'auto' }}
            >
              {!isLarge ? (
                <SmallHeader
                  title={title}
                  subtitle={subtitle}
                  handleClose={handleClose}
                  hasCloseButton={hasCloseButton}
                  bg={bg}
                />
              ) : (
                <LargeHeader
                  title={title}
                  subtitle={subtitle}
                  handleClose={handleClose}
                  bg={bg}
                  closeText={closeText}
                  hasCloseButton={hasCloseButton}
                />
              )}
              <Box
                mt={isLarge ? [0] : [2]}
                px={isLarge ? [4, 4, 10] : [5]}
                pt={isLarge ? [0] : [5]}
                pb={[13]} // just generally catering for ActionBar here
                bg={bg}
                sx={{ flex: '1 1 auto', ...containerSx }}
                {...contentProps}
              >
                <Box sx={{ maxWidth: maxWidth, marginX: 'auto' }}>
                  {children}
                </Box>
              </Box>
              {modalFooter && (
                <StyledFooter sx={{ flex: '0 1 auto' }} bg={bg}>
                  {modalFooter}
                </StyledFooter>
              )}
            </Flex>
          </AnimatedModal>
        </Overlay>
      ) : null}
    </ConditionalAnimation>
  );
};

export default Modal;
